/**
 * Mock data for Tekkr Program View (all 13 teams)
 * JSM Rollout Program - All Engineering Teams
 */

export interface AggregateMetric {
  current: number;
  target: number;
  trend: number[]; // 7-day trend
}

export interface Team {
  id: string;
  name: string;
  health: 'green' | 'yellow' | 'red';
  healthPercentage: number;
  channelCompliance: number;
  slaAdherence: number;
  timeToFirstResponse: number;
  overdueBacklog: number;
  clickable: boolean;
  trend: number[]; // 7-day trend for sparkline
}

export interface ProgramAlert {
  metric: string;
  threshold: number;
  direction: 'below' | 'above';
  scope: 'program' | 'team';
  owner: string;
  channels?: string[];
  teamId?: string;
}

export interface ProgramData {
  programName: string;
  aggregateMetrics: {
    avgChannelCompliance: AggregateMetric;
    avgSlaAdherence: AggregateMetric;
    totalOverdueBacklog: AggregateMetric;
    teamsAtRisk: {
      current: number;
      total: number;
    };
    healthScore: {
      current: number;
      target: number;
    };
  };
  teams: Team[];
  alerts: ProgramAlert[];
}

export const programMockData: ProgramData = {
  programName: "Jira Service Management Rollout Program - All Engineering Teams",
  aggregateMetrics: {
    avgChannelCompliance: {
      current: 78,
      target: 85,
      trend: [80, 79, 78, 78, 78, 77, 78]
    },
    avgSlaAdherence: {
      current: 81,
      target: 90,
      trend: [83, 82, 81, 81, 81, 80, 81]
    },
    totalOverdueBacklog: {
      current: 94,
      target: 65,
      trend: [70, 75, 82, 88, 91, 93, 94]
    },
    teamsAtRisk: {
      current: 5,
      total: 13
    },
    healthScore: {
      current: 72,
      target: 85
    }
  },
  teams: [
    {
      id: "frontend",
      name: "Frontend Team",
      health: "yellow",
      healthPercentage: 68,
      channelCompliance: 72,
      slaAdherence: 92,
      timeToFirstResponse: 26.2,
      overdueBacklog: 12,
      clickable: true,
      trend: [65, 68, 70, 71, 72, 73, 72]
    },
    {
      id: "backend",
      name: "Backend Team",
      health: "green",
      healthPercentage: 88,
      channelCompliance: 88,
      slaAdherence: 92,
      timeToFirstResponse: 18.5,
      overdueBacklog: 3,
      clickable: false,
      trend: [85, 86, 87, 88, 87, 88, 88]
    },
    {
      id: "devops",
      name: "DevOps Team",
      health: "red",
      healthPercentage: 55,
      channelCompliance: 55,
      slaAdherence: 60,
      timeToFirstResponse: 32.5,
      overdueBacklog: 18,
      clickable: false,
      trend: [60, 58, 57, 56, 55, 54, 55]
    },
    {
      id: "data",
      name: "Data Platform",
      health: "yellow",
      healthPercentage: 75,
      channelCompliance: 75,
      slaAdherence: 78,
      timeToFirstResponse: 24.5,
      overdueBacklog: 9,
      clickable: false,
      trend: [73, 74, 75, 76, 75, 74, 75]
    },
    {
      id: "mobile-ios",
      name: "Mobile iOS",
      health: "green",
      healthPercentage: 92,
      channelCompliance: 90,
      slaAdherence: 94,
      timeToFirstResponse: 16.2,
      overdueBacklog: 2,
      clickable: false,
      trend: [88, 89, 90, 91, 90, 90, 90]
    },
    {
      id: "mobile-android",
      name: "Mobile Android",
      health: "yellow",
      healthPercentage: 78,
      channelCompliance: 80,
      slaAdherence: 82,
      timeToFirstResponse: 22.8,
      overdueBacklog: 7,
      clickable: false,
      trend: [78, 79, 80, 81, 80, 80, 80]
    },
    {
      id: "infra",
      name: "Infrastructure",
      health: "red",
      healthPercentage: 60,
      channelCompliance: 62,
      slaAdherence: 65,
      timeToFirstResponse: 30.1,
      overdueBacklog: 15,
      clickable: false,
      trend: [68, 66, 64, 63, 62, 61, 62]
    },
    {
      id: "security",
      name: "Security Team",
      health: "green",
      healthPercentage: 94,
      channelCompliance: 92,
      slaAdherence: 95,
      timeToFirstResponse: 15.5,
      overdueBacklog: 1,
      clickable: false,
      trend: [90, 91, 92, 93, 92, 92, 92]
    },
    {
      id: "reporting",
      name: "Reporting Team",
      health: "red",
      healthPercentage: 58,
      channelCompliance: 58,
      slaAdherence: 63,
      timeToFirstResponse: 34.2,
      overdueBacklog: 20,
      clickable: false,
      trend: [65, 62, 60, 59, 58, 57, 58]
    },
    {
      id: "analytics",
      name: "Analytics Team",
      health: "yellow",
      healthPercentage: 76,
      channelCompliance: 78,
      slaAdherence: 80,
      timeToFirstResponse: 25.3,
      overdueBacklog: 10,
      clickable: false,
      trend: [76, 77, 78, 79, 78, 78, 78]
    },
    {
      id: "api",
      name: "API Team",
      health: "green",
      healthPercentage: 90,
      channelCompliance: 89,
      slaAdherence: 91,
      timeToFirstResponse: 17.3,
      overdueBacklog: 4,
      clickable: false,
      trend: [87, 88, 89, 90, 89, 89, 89]
    },
    {
      id: "integrations",
      name: "Integrations",
      health: "red",
      healthPercentage: 62,
      channelCompliance: 60,
      slaAdherence: 64,
      timeToFirstResponse: 31.8,
      overdueBacklog: 16,
      clickable: false,
      trend: [66, 64, 62, 61, 60, 59, 60]
    },
    {
      id: "qa",
      name: "QA Team",
      health: "yellow",
      healthPercentage: 74,
      channelCompliance: 76,
      slaAdherence: 79,
      timeToFirstResponse: 23.9,
      overdueBacklog: 8,
      clickable: false,
      trend: [74, 75, 76, 77, 76, 76, 76]
    }
  ],
  alerts: [
    {
      metric: "healthScore",
      threshold: 75,
      direction: "below",
      scope: "program",
      owner: "Jackie Chen",
      channels: ["Slack", "Email"]
    }
  ]
};

/**
 * 4-week historical data for program-level trend chart
 * 28 days of daily data for aggregate metrics
 */
export interface ProgramHistoricalData {
  date: string;
  avgChannelCompliance: number;
  avgSlaAdherence: number;
  totalOverdueBacklog: number;
  healthScore: number;
}

export const programHistoricalData: ProgramHistoricalData[] = [
  // Week 1
  { date: "Nov 18", avgChannelCompliance: 80, avgSlaAdherence: 83, totalOverdueBacklog: 65, healthScore: 76 },
  { date: "Nov 19", avgChannelCompliance: 81, avgSlaAdherence: 84, totalOverdueBacklog: 66, healthScore: 77 },
  { date: "Nov 20", avgChannelCompliance: 80, avgSlaAdherence: 83, totalOverdueBacklog: 68, healthScore: 76 },
  { date: "Nov 21", avgChannelCompliance: 80, avgSlaAdherence: 83, totalOverdueBacklog: 70, healthScore: 76 },
  { date: "Nov 22", avgChannelCompliance: 79, avgSlaAdherence: 82, totalOverdueBacklog: 72, healthScore: 75 },
  { date: "Nov 23", avgChannelCompliance: 80, avgSlaAdherence: 83, totalOverdueBacklog: 70, healthScore: 76 },
  { date: "Nov 24", avgChannelCompliance: 80, avgSlaAdherence: 83, totalOverdueBacklog: 70, healthScore: 76 },
  
  // Week 2
  { date: "Nov 25", avgChannelCompliance: 79, avgSlaAdherence: 82, totalOverdueBacklog: 73, healthScore: 75 },
  { date: "Nov 26", avgChannelCompliance: 79, avgSlaAdherence: 82, totalOverdueBacklog: 75, healthScore: 74 },
  { date: "Nov 27", avgChannelCompliance: 79, avgSlaAdherence: 82, totalOverdueBacklog: 77, healthScore: 74 },
  { date: "Nov 28", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 79, healthScore: 73 },
  { date: "Nov 29", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 80, healthScore: 73 },
  { date: "Nov 30", avgChannelCompliance: 79, avgSlaAdherence: 82, totalOverdueBacklog: 82, healthScore: 74 },
  { date: "Dec 1", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 82, healthScore: 73 },
  
  // Week 3
  { date: "Dec 2", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 84, healthScore: 73 },
  { date: "Dec 3", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 86, healthScore: 72 },
  { date: "Dec 4", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 88, healthScore: 72 },
  { date: "Dec 5", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 88, healthScore: 72 },
  { date: "Dec 6", avgChannelCompliance: 77, avgSlaAdherence: 80, totalOverdueBacklog: 90, healthScore: 71 },
  { date: "Dec 7", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 91, healthScore: 72 },
  { date: "Dec 8", avgChannelCompliance: 77, avgSlaAdherence: 80, totalOverdueBacklog: 91, healthScore: 71 },
  
  // Week 4 (most recent)
  { date: "Dec 9", avgChannelCompliance: 77, avgSlaAdherence: 80, totalOverdueBacklog: 92, healthScore: 71 },
  { date: "Dec 10", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 93, healthScore: 72 },
  { date: "Dec 11", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 93, healthScore: 72 },
  { date: "Dec 12", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 94, healthScore: 72 },
  { date: "Dec 13", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 94, healthScore: 72 },
  { date: "Dec 14", avgChannelCompliance: 77, avgSlaAdherence: 80, totalOverdueBacklog: 93, healthScore: 71 },
  { date: "Dec 15", avgChannelCompliance: 78, avgSlaAdherence: 81, totalOverdueBacklog: 94, healthScore: 72 },
];

